/**
 * The layout on screen, computed in layers — the last one wins:
 *
 *   preset → domain → screen → accessibility mode → constraints
 *
 * Constraints are what a mode guarantees whatever the layers did: `reachable` keeps the modules a
 * hand must touch below a line (motor mode: the lower half of a 1.9 m totem). If a module sits above
 * it, the top-level bands that hold one move down, after the others and before the bands pinned to
 * the bottom (`pin: 'bottom'`, the status bar). What could not be satisfied is reported.
 *
 * The result says who decided what (`decisions`), for the dev inspector.
 */
import { applyChange, nameOf, type LayoutChange } from './changes';
import { boxesOf, type Box } from './geometry';
import { CANVAS, modulesOf, type Preset } from './model';

export interface LayoutLayer {
  /** Who this layer is, as shown in the inspector: "domain:trains", "screen:seat_pick", "mode:motor". */
  source: string;
  changes: LayoutChange[];
}

export interface ReachableConstraint {
  kind: 'reachable';
  source: string;
  modules: string[];
  /** Canvas y the top of each module must not be above. */
  minTop: number;
}

export type LayoutConstraint = ReachableConstraint;

export interface LayoutDecision {
  source: string;
  what: string;
  applied: boolean;
  note?: string;
}

export interface ResolvedLayout {
  preset: Preset;
  boxes: Map<string, Box>;
  decisions: LayoutDecision[];
  /** Things the shell must show as a problem in dev: unsatisfied constraints, unknown modules. */
  problems: string[];
}

export function resolveLayout(
  base: Preset,
  layers: LayoutLayer[],
  constraints: LayoutConstraint[] = [],
  knownModules?: ReadonlySet<string>,
  /** The area the layout fills (the whole canvas). */
  area: Box = { x: 0, y: 0, ...CANVAS },
): ResolvedLayout {
  let preset = base;
  const decisions: LayoutDecision[] = [{ source: `preset:${base.id}`, what: 'base', applied: true }];
  for (const layer of layers) {
    for (const change of layer.changes) {
      const r = applyChange(preset, change);
      preset = r.preset;
      decisions.push({ source: layer.source, what: describe(change), ...r.outcome });
    }
  }
  const problems: string[] = [];
  for (const c of constraints) {
    preset = enforceReachable(preset, c, decisions, problems, area);
  }
  if (knownModules) {
    for (const m of modulesOf(preset.root)) {
      if (!knownModules.has(m)) problems.push(`module "${m}" is not registered`);
    }
  }
  return { preset, boxes: boxesOf(preset.root, area), decisions, problems };
}

function enforceReachable(
  preset: Preset,
  c: ReachableConstraint,
  decisions: LayoutDecision[],
  problems: string[],
  area: Box,
): Preset {
  const tooHigh = (p: Preset) => {
    const boxes = boxesOf(p.root, area);
    return c.modules.filter((m) => {
      const b = boxes.get(m);
      return b !== undefined && b.y < c.minTop;
    });
  };
  let current = preset;
  const first = tooHigh(current);
  if (first.length === 0) return current;
  if (current.root.direction === 'column') {
    const bands = current.root.children;
    const pinned = (band: (typeof bands)[number]) => band.pin === 'bottom';
    const holds = (band: (typeof bands)[number]) => !pinned(band) && first.some((m) => modulesOf(band).includes(m));
    const reordered = [
      ...bands.filter((b) => !pinned(b) && !holds(b)),
      ...bands.filter(holds),
      ...bands.filter(pinned),
    ];
    current = { ...current, root: { ...current.root, children: reordered } };
    decisions.push({
      source: c.source,
      what: `reachable: moved ${bands.filter(holds).map(nameOf).join(', ')} to the bottom`,
      applied: true,
    });
  }
  for (const m of tooHigh(current)) {
    problems.push(`${c.source}: "${m}" stays above y=${c.minTop}`);
  }
  return current;
}

function describe(change: LayoutChange): string {
  switch (change.op) {
    case 'hide': return `hide ${change.module}`;
    case 'resize': return `resize ${change.target} to ${'px' in change.size ? `${change.size.px}px` : `${change.size.fr}fr`}`;
    case 'move': {
      const where = change.before ? ` before ${change.before}` : change.after ? ` after ${change.after}` : change.index === undefined ? '' : ` at ${change.index}`;
      return `move ${change.module} into ${change.into}${where}`;
    }
    case 'order': return `order ${change.split}: ${change.children.join(', ')}`;
    case 'scene': return `scene ${change.scene}`;
    case 'stageOffset': return `stage ${change.y}px down`;
    case 'add': return `add ${change.module} into ${change.into}`;
  }
}
